/**
 * WHAT THE OWNER'S SYSTEM RECEIVES for an answer, as the sidecar works it out
 * (`ai-sidecar/src/tasks/decide.ts` `outwardValue`, P-2.3). PURE. No Vue, no store, no I/O.
 *
 * The engine keeps a chosen option's ID inside a run and sends its outward value: the owner's
 * `value` when she set one, else the id when a person wrote it (`sharq`), else the option's
 * English words, then its Arabic ones. Never "o2". This file is that rule for the screens that
 * show it (the "What your system receives" box, "sends: Haircut" on the wiring screen), so the
 * builder and the engine cannot say two different things.
 */

/** An option id the builder minted (`o1`, `o2`): a handle, never a value. */
const MINTED_OPTION_ID = /^o[0-9]+$/;

const text = value => (typeof value === 'string' ? value.trim() : '');

/** What a choice sends. `''` only for an option with no words and a minted id. */
export const optionSends = option => {
  const own = text(option?.value);
  if (own !== '') return own;
  const id = text(option?.id);
  if (id !== '' && !MINTED_OPTION_ID.test(id)) return id;
  return text(option?.labelEn) || text(option?.labelAr);
};

/** Every connection input, across every step, that reads the chat key `key`. */
const readsKey = (steps, key) =>
  steps.some(
    step =>
      (step?.type === 'call' || step?.type === 'choose_live') &&
      Object.values(step.inputs ?? {}).some(
        source => source && typeof source === 'object' && source.key === key
      )
  );

/**
 * Whether an answer to `step` leaves the chat: it is saved on the customer, or a connection is
 * sent it. Only then is "What your system receives" worth a box (P-2.3).
 */
export const answerLeavesChat = (step, steps) => {
  if (text(step?.saveTo?.contact) !== '') return true;
  const chat = text(step?.saveTo?.chat);
  return chat !== '' && readsKey(Array.isArray(steps) ? steps : [], chat);
};

/**
 * `{[chatKey]: ['Haircut', 'Colour']}` for every key a `choose` step writes, so the wiring
 * screen can say what a detail it sends will carry.
 */
export const sendsByKey = steps => {
  const out = {};
  (Array.isArray(steps) ? steps : []).forEach(step => {
    const chat = text(step?.saveTo?.chat);
    if (step?.type !== 'choose' || chat === '' || out[chat]) return;
    out[chat] = (step.options ?? []).map(optionSends).filter(Boolean);
  });
  return out;
};

export default optionSends;
